import {
  Dumbbell, Footprints, Bike, Waves, Layers, ClipboardList,
} from "lucide-react";
import { computeTargetWeight, formatIntensityLine } from "@/lib/strength";

export const DISCIPLINE_IDS = ["strength", "running", "cycling", "swimming", "hybrid", "general"];

export const DISCIPLINES = {
  strength: {
    id: "strength",
    label: "Force & musculation",
    shortLabel: "Force",
    icon: Dumbbell,
    color: "bg-slate-900 text-white",
    ring: "ring-slate-900",
    light: "bg-slate-100 text-slate-800",
    border: "border-slate-200",
    accentBorder: "border-l-slate-800",
    accent: "text-slate-900",
    description: "Hypertrophie, powerlifting, préparation physique en salle",
    logLabel: "Séries & charges",
    usesLiftMaxes: true,
    usesSportRefs: false,
  },
  running: {
    id: "running",
    label: "Course à pied",
    shortLabel: "Course",
    icon: Footprints,
    color: "bg-emerald-600 text-white",
    ring: "ring-emerald-500",
    light: "bg-emerald-50 text-emerald-800",
    border: "border-emerald-200",
    accentBorder: "border-l-emerald-500",
    accent: "text-emerald-700",
    description: "Road, trail, VMA, seuil, endurance",
    logLabel: "Distance & allure",
    usesLiftMaxes: false,
    usesSportRefs: true,
    sportRefKeys: ["threshold_pace_sec_km", "vma_kmh"],
  },
  cycling: {
    id: "cycling",
    label: "Vélo",
    shortLabel: "Vélo",
    icon: Bike,
    color: "bg-sky-600 text-white",
    ring: "ring-sky-500",
    light: "bg-sky-50 text-sky-800",
    border: "border-sky-200",
    accentBorder: "border-l-sky-500",
    accent: "text-sky-700",
    description: "Route, home trainer, FTP, zones puissance",
    logLabel: "Puissance & durée",
    usesLiftMaxes: false,
    usesSportRefs: true,
    sportRefKeys: ["ftp_w"],
  },
  swimming: {
    id: "swimming",
    label: "Natation",
    shortLabel: "Natation",
    icon: Waves,
    color: "bg-cyan-600 text-white",
    ring: "ring-cyan-500",
    light: "bg-cyan-50 text-cyan-800",
    border: "border-cyan-200",
    accentBorder: "border-l-cyan-500",
    accent: "text-cyan-700",
    description: "Piscine, CSS, séries au 100 m",
    logLabel: "Distance & temps",
    usesLiftMaxes: false,
    usesSportRefs: true,
    sportRefKeys: ["css_sec_100m"],
  },
  hybrid: {
    id: "hybrid",
    label: "Hybride / Triathlon",
    shortLabel: "Hybride",
    icon: Layers,
    color: "bg-violet-600 text-white",
    ring: "ring-violet-500",
    light: "bg-violet-50 text-violet-800",
    border: "border-violet-200",
    accentBorder: "border-l-violet-500",
    accent: "text-violet-700",
    description: "Multi-sports : force + endurance dans un même plan",
    logLabel: "Mixte",
    usesLiftMaxes: true,
    usesSportRefs: true,
    sportRefKeys: ["ftp_w", "threshold_pace_sec_km", "css_sec_100m"],
  },
  general: {
    id: "general",
    label: "Général / Autre",
    shortLabel: "Général",
    icon: ClipboardList,
    color: "bg-amber-600 text-white",
    ring: "ring-amber-500",
    light: "bg-amber-50 text-amber-800",
    border: "border-amber-200",
    accentBorder: "border-l-amber-500",
    accent: "text-amber-700",
    description: "Plan libre sans métriques imposées",
    logLabel: "Durée & notes",
    usesLiftMaxes: false,
    usesSportRefs: false,
  },
};

export const SPORT_REF_DEFS = {
  ftp_w: { label: "FTP", unit: "W", placeholder: "250", step: 1 },
  threshold_pace_sec_km: { label: "Allure seuil", unit: "min/km", placeholder: "4:30", format: "pace_km" },
  css_sec_100m: { label: "CSS", unit: "/100m", placeholder: "1:35", format: "pace_100m" },
  vma_kmh: { label: "VMA", unit: "km/h", placeholder: "16", step: 0.1 },
};

export const ZONE_OPTIONS = [
  { value: "", label: "—" },
  { value: "z1", label: "Z1 Récup" },
  { value: "z2", label: "Z2 Endurance" },
  { value: "z3", label: "Z3 Tempo" },
  { value: "z4", label: "Z4 Seuil" },
  { value: "z5", label: "Z5 VMA" },
];

export function getDiscipline(id) {
  return DISCIPLINES[id] || DISCIPLINES.strength;
}

export function normalizeDiscipline(id) {
  return DISCIPLINE_IDS.includes(id) ? id : "strength";
}

export function resolveExerciseModality(exercise, planDiscipline) {
  const mod = exercise?.modality || planDiscipline;
  if (planDiscipline === "hybrid") {
    return normalizeDiscipline(exercise?.modality || "general");
  }
  if (planDiscipline === "general") return "general";
  return normalizeDiscipline(mod === "hybrid" ? planDiscipline : mod);
}

export function emptyExercise(planDiscipline = "strength", modality = null) {
  const mod = modality || (planDiscipline === "hybrid" ? "strength" : planDiscipline);
  const base = { name: "", modality: mod, sets: "", reps: "", rest: "", rpe: "", notes: "" };

  switch (mod) {
    case "running":
      return { ...base, distance_km: "", target_pace: "", target_pct_threshold: "", zone: "", duration_min: "" };
    case "cycling":
      return { ...base, duration_min: "", target_pct_ftp: "", target_watts: "", distance_km: "" };
    case "swimming":
      return { ...base, distance_m: "", target_pace_100m: "", duration_min: "" };
    case "general":
      return { ...base, duration_min: "" };
    case "strength":
    default:
      return {
        ...base,
        lift_key: "",
        intensity_pct: "",
        intensity_mode: "percent_tm",
      };
  }
}

export function normalizeExercise(ex = {}, planDiscipline = "strength") {
  const mod = resolveExerciseModality(ex, planDiscipline);
  return { ...emptyExercise(planDiscipline, mod), ...ex, modality: mod };
}

export function isRestDay(day) {
  const label = String(day?.day || "").toLowerCase();
  if (/repos|rest|off|recovery|récup|recup/.test(label)) return true;
  return !(day?.exercises || []).some((ex) => String(ex?.name || "").trim());
}

export function parsePaceKmToSec(str) {
  if (!str) return null;
  const s = String(str).trim();
  if (/^\d+(\.\d+)?$/.test(s)) return Math.round(parseFloat(s) * 60);
  const m = s.match(/^(\d+):(\d{1,2})$/);
  if (m) return parseInt(m[1], 10) * 60 + parseInt(m[2], 10);
  return null;
}

export function formatPaceKm(sec) {
  if (!sec || Number.isNaN(sec)) return "—";
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  return `${m}:${String(s).padStart(2, "0")}/km`;
}

export function parsePace100mToSec(str) {
  if (!str) return null;
  const s = String(str).trim();
  const m = s.match(/^(\d+):(\d{1,2})$/);
  if (m) return parseInt(m[1], 10) * 60 + parseInt(m[2], 10);
  if (/^\d+(\.\d+)?$/.test(s)) return Math.round(parseFloat(s));
  return null;
}

export function formatPace100m(sec) {
  if (!sec || Number.isNaN(sec)) return "—";
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  return `${m}:${String(s).padStart(2, "0")}/100m`;
}

export function refsMapFromApi(refs = []) {
  return Object.fromEntries(
    (refs || []).filter((r) => r.ref_key && r.value != null).map((r) => [r.ref_key, r.value])
  );
}

export function computeExerciseTarget(exercise, planDiscipline, { liftMaxes = [], sportRefs = [] } = {}) {
  const mod = resolveExerciseModality(exercise, planDiscipline);
  const refs = refsMapFromApi(sportRefs);

  if (mod === "strength") {
    const w = computeTargetWeight(exercise, liftMaxes);
    return w ? { type: "weight", value: w, label: `${w} kg` } : null;
  }

  if (mod === "running") {
    const pct = parseFloat(exercise.target_pct_threshold);
    const base = refs.threshold_pace_sec_km;
    if (base && pct) {
      const pace = Math.round(base * (100 / pct));
      return { type: "pace", value: pace, label: formatPaceKm(pace) };
    }
    const pace = parsePaceKmToSec(exercise.target_pace);
    if (pace) return { type: "pace", value: pace, label: formatPaceKm(pace) };
    return null;
  }

  if (mod === "cycling") {
    const pct = parseFloat(exercise.target_pct_ftp);
    const ftp = refs.ftp_w;
    if (ftp && pct) {
      const watts = Math.round(ftp * pct / 100);
      return { type: "power", value: watts, label: `${watts} W (${pct}% FTP)` };
    }
    const w = parseFloat(exercise.target_watts);
    if (w) return { type: "power", value: w, label: `${w} W` };
    return null;
  }

  if (mod === "swimming") {
    const css = refs.css_sec_100m;
    const offset = parseFloat(exercise.css_offset_sec);
    if (css && !Number.isNaN(offset)) {
      const pace = Math.round(css + offset);
      return { type: "pace_100m", value: pace, label: `CSS${offset >= 0 ? "+" : ""}${offset}s (${formatPace100m(pace)})` };
    }
    const pace = parsePace100mToSec(exercise.target_pace_100m);
    if (pace) return { type: "pace_100m", value: pace, label: formatPace100m(pace) };
    return null;
  }

  return null;
}

export function formatExercisePrescription(exercise, planDiscipline, ctx = {}) {
  const mod = resolveExerciseModality(exercise, planDiscipline);
  const target = computeExerciseTarget(exercise, planDiscipline, ctx);
  const parts = [];

  if (mod === "strength") {
    const line = formatIntensityLine(exercise, ctx.liftMaxes || []);
    if (line) parts.push(line);
    else if (exercise.sets && exercise.reps) parts.push(`${exercise.sets}×${exercise.reps}`);
  } else if (mod === "running") {
    if (exercise.distance_km) parts.push(`${exercise.distance_km} km`);
    if (exercise.zone) parts.push(exercise.zone.toUpperCase());
    if (target) parts.push(target.label);
    else if (exercise.target_pace) parts.push(`@ ${exercise.target_pace}`);
    if (exercise.duration_min) parts.push(`${exercise.duration_min} min`);
  } else if (mod === "cycling") {
    if (exercise.duration_min) parts.push(`${exercise.duration_min} min`);
    if (target) parts.push(target.label);
    if (exercise.distance_km) parts.push(`${exercise.distance_km} km`);
  } else if (mod === "swimming") {
    if (exercise.distance_m) parts.push(`${exercise.distance_m} m`);
    if (target) parts.push(target.label);
    if (exercise.sets && parseInt(exercise.sets, 10) > 1) parts.push(`${exercise.sets} séries`);
  } else if (mod === "general") {
    if (exercise.duration_min) parts.push(`${exercise.duration_min} min`);
    if (exercise.notes) parts.push(exercise.notes);
  }

  if (exercise.rpe) parts.push(`RPE ${exercise.rpe}`);
  if (exercise.rest && mod === "strength") parts.push(`repos ${exercise.rest}`);

  return parts.length ? parts.join(" · ") : null;
}

export function disciplineUsesIntervals(planDiscipline, exercise) {
  const mod = resolveExerciseModality(exercise, planDiscipline);
  return mod === "strength" || (parseInt(exercise.sets, 10) || 0) > 1;
}

export function parseIntervalCount(exercise, planDiscipline) {
  const mod = resolveExerciseModality(exercise, planDiscipline);
  if (mod === "general") return 1;
  const n = parseInt(exercise.sets, 10);
  return n > 0 ? n : (mod === "strength" ? 3 : 1);
}